import { isPromise, wrapAsyncFn } from "@faire/typescript";
import { useEffect, useRef, useState, type MouseEvent } from "react";

import { useDelayedCallback } from "./useDelayedCallback";

const PENDING_DELAY_MS = 1000;

export type AsyncClickHandler = (
  e: MouseEvent<HTMLElement>
) => void | Promise<unknown>;

export type AsyncClickHandlerResult = {
  handleClick: (e: MouseEvent<HTMLElement>) => Promise<void>;
  isDisabled: boolean;
  isPending: boolean;
  isPendingDelayed: boolean;
};

/**
 * Tracks pending state while `onClick` returns a promise. The click handler is
 * disabled for that duration. `isPendingDelayed` is true immediately when
 * `pending` is passed in, and after 1000ms when pending is caused by the click
 * promise.
 */
export const useAsyncClickHandler = (
  disabled: boolean | undefined,
  pending: boolean | undefined,
  onClick?: AsyncClickHandler
): AsyncClickHandlerResult => {
  const [internalPending, setInternalPending] = useState(false);
  const [pendingDelayExpired, setPendingDelayExpired] = useState(false);

  const isMounted = useRef(true);

  const isDisabled = disabled || pending || internalPending;
  const isPending = pending || internalPending;

  const setDelayedPending = useDelayedCallback(() => {
    setPendingDelayExpired(true);
  });

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleClick = async (e: MouseEvent<HTMLElement>) => {
    if (isDisabled) {
      e.preventDefault();
      return;
    }

    const result = onClick?.(e);

    if (isPromise(result)) {
      setInternalPending(true);
      const { cancel: cancelSetDelayedPending } =
        setDelayedPending(PENDING_DELAY_MS);

      await wrapAsyncFn(() => result, {
        after: () => {
          cancelSetDelayedPending();

          if (isMounted.current) {
            setInternalPending(false);
            setPendingDelayExpired(false);
          }
        },
      });
    }
  };

  const isPendingDelayed = pending || (internalPending && pendingDelayExpired);

  return { handleClick, isDisabled, isPending, isPendingDelayed };
};
